// @file Thin wrapper over the global content picker store for the combined AV recorder.

import { ALL_AV_SOURCES, ContentPickerSource } from '@@/bits/content_picker'
import { useContentPickerAvAudioRecorderStore } from '@@/pinia/useContentPickerAvAudioRecorderStore'
import { useContentPickerAvPhotoBoothStore } from '@@/pinia/useContentPickerAvPhotoBoothStore'
import { useContentPickerAvScreenRecorderStore } from '@@/pinia/useContentPickerAvScreenRecorderStore'
import { useContentPickerAvVideoRecorderStore } from '@@/pinia/useContentPickerAvVideoRecorderStore'
import { useGlobalContentPickerStore } from '@@/pinia/useGlobalContentPickerStore'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const useContentPickerAvRecorderStore = defineStore('contentPickerAvRecorderStore', () => {
  const globalStore = useGlobalContentPickerStore()
  const videoStore = useContentPickerAvVideoRecorderStore()
  const screenStore = useContentPickerAvScreenRecorderStore()
  const audioStore = useContentPickerAvAudioRecorderStore()
  const photoStore = useContentPickerAvPhotoBoothStore()

  // #region Session
  const enabledSources = computed<ContentPickerSource[]>(() => {
    const session = globalStore.currentSession
    if (session == null) return [...ALL_AV_SOURCES]
    return session.enabledSources.filter((s) => ALL_AV_SOURCES.includes(s))
  })

  const isVisible = computed<boolean>(() => {
    const session = globalStore.currentSession
    if (session == null) return false

    // Hide the modal for the screen recording session while recording is in progress
    // (only the status bar should be visible). Other sessions are unaffected.
    if (screenStore.isScreenRecordingInProgress && session === globalStore.globalScreenRecordingSession) return false

    const ac = session.activeComponent
    return ac != null && enabledSources.value.includes(ac)
  })

  const activeSource = computed<ContentPickerSource>(() => {
    return (globalStore.currentSession?.activeComponent as ContentPickerSource) ?? enabledSources.value[0]
  })

  function close(): void {
    globalStore.currentPickerConfig?.handlers?.onClose?.()
    globalStore.endCurrentSession()
  }

  function setActiveSource(source: ContentPickerSource): void {
    globalStore.setCurrentSessionActiveComponent(source)
  }

  // #endregion Session

  // #region Close guard
  function handleClose(): void {
    if (videoStore.hasUnsavedVideoWork) {
      videoStore.showDiscardDialog()
      return
    }
    if (screenStore.hasUnsavedScreenWork) {
      screenStore.showDiscardDialog()
      return
    }
    if (audioStore.hasUnsavedAudioWork) {
      audioStore.showDiscardDialog()
      return
    }
    if (photoStore.hasUnsavedPhotoWork) {
      photoStore.showDiscardDialog()
      return
    }
    close()
  }
  // #endregion Close guard

  // #region Frame
  const xAvTabs = computed<boolean>(() => {
    return (
      !videoStore.hasStartedRecordingVideo &&
      !screenStore.hasStartedRecordingScreen &&
      !audioStore.hasStartedRecordingAudio &&
      !photoStore.hasStartedPhotoCapture
    )
  })

  const zIndexOverride = ref<number | null>(null)
  function setZIndexOverride(value: number | null): void {
    zIndexOverride.value = value
  }
  // #endregion Frame

  return {
    isVisible,
    activeSource,
    enabledSources,
    xAvTabs,
    close,
    setActiveSource,
    handleClose,
    zIndexOverride,
    setZIndexOverride,
  }
})

export { useContentPickerAvRecorderStore }
